import {
  ButtonPrimeReact,
  DropdownPrimeReact,
  PrimeReactModal,
  TagERP,
} from "@finanedi.es/storybook"
import {
  faExclamationTriangle,
  faInfoCircle,
  faLock,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  getBloqueoPeriodoContable,
  putBloqueoPeriodoContable,
} from "api/BloqueoPeriodoContable.service"
import { UpdateBloqueoPeriodoContableCommandProps } from "api/Commands/UpdateBloqueoPeriodoContableCommand"
import notification from "common/utils/notification"
import { INITIAL_PERIOD_CIERRE_CONTABLE } from "constants/numbers"
import { MODAL_STYLES } from "constants/primereactStyles"
import { DropdownChangeParams } from "primereact/dropdown"
import { Message } from "primereact/message"
import { useEffect, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { generateYearsRange } from "utils/generateYearsRange"
import Spinner from "../Spinner"
import { ToggleSwitch } from "../Toogle"

interface ModalContent {
  showModalCierreContable: boolean
  onCancel: () => void
}

interface SwitchState {
  anyo: number
  bloqueos: number[]
}

const ModalContent = ({ showModalCierreContable, onCancel }: ModalContent) => {
  const { t } = useTranslation()
  const drodpdownList = generateYearsRange(INITIAL_PERIOD_CIERRE_CONTABLE)
  const [yearSelected, setYearSelected] = useState(drodpdownList[0])
  const [switchesByYear, setSwitchesByYear] = useState<SwitchState[]>([])

  const queryClient = useQueryClient()
  const {
    data: bloqueoPeriodoContable,
    isLoading: bloqueoPeriodoContableIsLoading,
  } = useQuery({
    queryKey: ["getBloqueoPeriodoContable", yearSelected],
    queryFn: () => getBloqueoPeriodoContable(yearSelected),
    refetchOnWindowFocus: false,
    enabled:
      showModalCierreContable &&
      !switchesByYear.find((year) => year.anyo === yearSelected),
    retry: false,
  })
  const isDecemberToBeBlocked = switchesByYear
    .find((year) => year.anyo === yearSelected)
    ?.bloqueos?.includes(12)
  const wasDecemberAlreadyBlocked = bloqueoPeriodoContable?.meses?.includes(12)
  const { mutateAsync: updateBloqueoPeriodoContable } = useMutation({
    mutationFn: async (
      updateBloqueoPeriodo: UpdateBloqueoPeriodoContableCommandProps
    ) => putBloqueoPeriodoContable(updateBloqueoPeriodo),
    onSuccess: () => {
      notification.success({
        body: `${t("notification.accountingCloseUpdate")}`,
      })
      queryClient.removeQueries({ queryKey: ["getBloqueoPeriodoContable"] })
    },
  })

  const MONTHS = [
    t("dropdownDatesRanges.january"),
    t("dropdownDatesRanges.february"),
    t("dropdownDatesRanges.march"),
    t("dropdownDatesRanges.april"),
    t("dropdownDatesRanges.may"),
    t("dropdownDatesRanges.june"),
    t("dropdownDatesRanges.july"),
    t("dropdownDatesRanges.august"),
    t("dropdownDatesRanges.september"),
    t("dropdownDatesRanges.october"),
    t("dropdownDatesRanges.november"),
    t("dropdownDatesRanges.december"),
  ]
  const handleYearSelection = (e: DropdownChangeParams) => {
    e.preventDefault()
    setYearSelected(e.value)
  }

  const toggleMonthSwitchNew = (year: number, month: number) => {
    setSwitchesByYear((prev: SwitchState[]) => {
      return prev.map((item) => {
        if (item.anyo === year) {
          const isMonthBlocked = item.bloqueos.includes(month)

          const updatedMonths = isMonthBlocked
            ? item.bloqueos.filter((m: number) => m !== month)
            : [...item.bloqueos, month]

          return { ...item, bloqueos: updatedMonths }
        }

        return item
      })
    })
  }

  const selectAllMonths = () => {
    setSwitchesByYear((prev: SwitchState[]) => {
      return prev.map((item: SwitchState) => {
        if (item.anyo === yearSelected) {
          return {
            ...item,
            bloqueos: MONTHS.map((_, index) => index + 1),
          }
        }
        return item
      })
    })
  }
  const handleSubmit = async () => {
    const dataToSubmit = {
      periodos: switchesByYear.map((yearData) => ({
        anyo: yearData.anyo,
        bloqueos: MONTHS.map((_, index) => {
          const mesNumero = index + 1
          const mesData = yearData.bloqueos.find((mes) => mes === mesNumero)
          return {
            mes: mesNumero,
            bloqueado: Boolean(mesData),
          }
        }),
      })),
    }

    const executeUpdatePeriodoContable = async () => {
      await updateBloqueoPeriodoContable(dataToSubmit)
      setSwitchesByYear([])
    }

    switchesByYear.length > 0 && executeUpdatePeriodoContable()
  }

  useEffect(() => {
    bloqueoPeriodoContable &&
      setSwitchesByYear((prev: SwitchState[]) => {
        const yearData = prev.find(
          (item: SwitchState) => item.anyo === yearSelected
        )

        if (yearData) {
          return prev
        }

        const newYearData = {
          anyo: yearSelected,
          bloqueos: bloqueoPeriodoContable.meses || [],
        }
        return [...prev, newYearData]
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bloqueoPeriodoContable])
  return (
    <div>
      <Message
        className='tw-p-3 tw-text-sm tw-mb-4'
        content={
          <div className='tw-flex tw-flex-row tw-text-sm'>
            <div className='tw-flex tw-items-center tw-mr-3'>
              <FontAwesomeIcon
                className='tw-font-bold tw-w-4 tw-h-4'
                icon={faInfoCircle}
              />
            </div>
            <div>
              <p className='tw-font-medium tw-text-sm'>
                {t("diario.accountingCloseModal.infoAccountingCloseTitle")}
              </p>
              <Trans
                i18nKey='diario.accountingCloseModal.infoAccountingCloseText'
                components={[
                  <span
                    className='tw-font-medium'
                    key='0'
                  />,
                ]}
                values={{ year: yearSelected }}
              />
            </div>
          </div>
        }
      />
      <div className='tw-flex tw-justify-between tw-items-center tw-pb-6'>
        <div>
          <p className='tw-pb-2'>{t("diario.accountingCloseModal.year")}</p>
          <DropdownPrimeReact
            id='listaAñoCierreContable'
            name='listaAñoCierreContable'
            className='!tw-w-32'
            options={drodpdownList}
            value={yearSelected}
            onChange={handleYearSelection}
          />
        </div>
        <p
          onClick={() => selectAllMonths()}
          className='tw-text-text-hyperlinks tw-cursor-pointer'
        >
          {t("diario.accountingCloseModal.closeYear")}
        </p>
      </div>
      <div className='tw-pb-2'>
        {t("diario.accountingCloseModal.selectMonths")}
      </div>
      <div className='tw-h-91.5'>
        {bloqueoPeriodoContableIsLoading ? (
          <div className='tw-flex tw-items-center tw-mx-auto tw-h-full'>
            <Spinner />
          </div>
        ) : (
          <div className='tw-grid tw-grid-cols-2 tw-grid-rows-6 tw-gap-x-12'>
            {MONTHS.map((month: string, index: number) => {
              const monthIsActive = switchesByYear
                .filter((year) => year.anyo === yearSelected)?.[0]
                ?.bloqueos?.includes(index + 1)
              const monthLocked = bloqueoPeriodoContable?.meses?.includes(
                index + 1
              )
              return (
                <div
                  key={month}
                  className='tw-flex tw-justify-between tw-items-center tw-border-b tw-border-neutral-25 tw-border-dashed tw-py-4'
                >
                  <p className='tw-capitalize'>{month}</p>
                  {monthLocked ? (
                    <TagERP
                      color='primary'
                      value={t("diario.accountingCloseModal.close")}
                      icon={<FontAwesomeIcon icon={faLock} />}
                      className='!tw-rounded-md'
                    />
                  ) : (
                    <ToggleSwitch
                      checkedState={monthIsActive}
                      onChangeHandler={() =>
                        toggleMonthSwitchNew(yearSelected, index + 1)
                      }
                    />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
      {isDecemberToBeBlocked && !wasDecemberAlreadyBlocked && (
        <Message
          className='tw-p-3'
          severity='warn'
          content={
            <div className='tw-flex tw-flex-row tw-text-sm'>
              <div className='tw-flex tw-items-center tw-mr-3'>
                <FontAwesomeIcon
                  className='tw-font-bold tw-w-4 tw-h-4'
                  icon={faExclamationTriangle}
                />
              </div>
              <div>
                <p className='tw-font-medium tw-text-sm'>
                  {t("diario.accountingCloseModal.warningAccountingCloseTitle")}
                </p>
                <Trans
                  i18nKey='diario.accountingCloseModal.warningAccountingCloseText'
                  components={[
                    <span
                      className='tw-font-medium'
                      key='0'
                    />,
                  ]}
                  values={{ year: yearSelected }}
                />
              </div>
            </div>
          }
        />
      )}
      <div className='tw-flex tw-flex-row tw-justify-end tw-items-center tw-pt-6'>
        <ButtonPrimeReact
          onClick={() => {
            onCancel()
          }}
          className='!tw-h-12 !tw-px-4 !tw-flex !tw-justify-center !tw-items-center !tw-border-transparent !tw-bg-transparent !tw-text-primary !tw-rounded'
        >
          {t("diario.accountingCloseModal.cancel")}
        </ButtonPrimeReact>
        <ButtonPrimeReact
          onClick={() => {
            handleSubmit()
            onCancel()
          }}
          className='!tw-h-12 !tw-px-4 !tw-flex !tw-justify-center !tw-items-center !tw-border-primary !tw-bg-primary !tw-text-white !tw-rounded !tw-ml-4'
        >
          {t("diario.accountingCloseModal.apply")}
        </ButtonPrimeReact>
      </div>
    </div>
  )
}

const ModalCierreContable = ({
  showModalCierreContable,
  onCancel,
}: {
  showModalCierreContable: boolean
  onCancel: () => void
}) => {
  const { t } = useTranslation()

  return (
    <PrimeReactModal
      visible={showModalCierreContable}
      baseZIndex={20}
      ptContext={MODAL_STYLES}
      closable={false}
      keepInViewport={false}
      className='!tw-w-192 tw-rounded-lg tw-py-6 tw-px-6 tw-bg-white'
      header={
        <div className='tw-flex tw-justify-between tw-items-center tw-mb-6 tw-h-10'>
          <p className='tw-text-2xl tw-font-medium tw-text-primary tw-m-0'>
            {t("diario.accountingCloseModal.title")}
          </p>
          <ButtonPrimeReact
            className='!tw-p-0 !tw-min-w-0 !tw-bg-white !tw-border-none !tw-text-black'
            onClick={() => {
              onCancel()
            }}
          >
            <FontAwesomeIcon
              icon={["fal", "xmark"]}
              className='tw-w-5 tw-h-5'
            />
          </ButtonPrimeReact>
        </div>
      }
    >
      <ModalContent
        showModalCierreContable={showModalCierreContable}
        onCancel={onCancel}
      />
    </PrimeReactModal>
  )
}

export default ModalCierreContable
